import { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Link
} from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { logger } from '@/utils/logger'
import { _ENV_VARIABLES } from '@/utils/constants'

const LoginForm = () => {
  const { signIn, signInWithGoogle, loading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)

  const handleGoogleSignIn = async () => {
    setError(null)
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
    } catch (err: any) {
      logger.error('Error signing in with Google', err, {
        component: 'LoginForm',
        action: 'handleGoogleSignIn',
        metadata: { errorCode: err?.code }
      })

      const errorCode = err?.code
      let errorMessage = 'Failed to sign in with Google. Please try again.'

      if (errorCode === 'auth/user-disabled') {
        errorMessage = 'This Google account has been disabled. Please contact support.'
      } else if (errorCode === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in cancelled. Please try again.'
      } else if (errorCode === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked by your browser. Please allow popups and try again.'
      } else if (errorCode) {
        errorMessage = `Google sign-in failed: ${err.message}`
      }

      setError(errorMessage)
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email || !password) {
      setError('Please enter email and password')
      return
    }

    try {
      await signIn(email, password)
    } catch (err: any) {
      logger.error('Error signing in with email', err, {
        component: 'LoginForm',
        action: 'handleSubmit',
        metadata: { errorCode: err?.code }
      })

      const errorCode = err?.code
      let errorMessage = 'Failed to sign in. Please try again.'

      if (errorCode === 'auth/invalid-email') {
        errorMessage = 'Invalid email address'
      } else if (errorCode === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact support.'
      } else if (
        errorCode === 'auth/user-not-found' ||
        errorCode === 'auth/wrong-password' ||
        errorCode === 'auth/invalid-credential'
      ) {
        errorMessage = 'Invalid email or password'
      } else if (errorCode) {
        errorMessage = `Sign-in failed: ${err.message}`
      }

      setError(errorMessage)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        px: 2,
        py: 3,
        bgcolor: 'grey.100'
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 360,
          boxShadow: 3,
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Bridge18 Logo"
              sx={{
                width: 64,
                height: 64,
                mb: 1.5,
                borderRadius: 2
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: 'primary.main' }}
            >
              Bridge18
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sign in to view load details
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Google Sign In Button */}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            sx={{
              height: 44,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              mb: 2,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'grey.400',
                bgcolor: 'grey.50'
              }
            }}
          >
            {googleLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <>
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  sx={{ width: 20, height: 20, mr: 1.5 }}
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </Box>
                Sign in with Google
              </>
            )}
          </Button>

          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ mb: 2 }}
              InputProps={{
                sx: { borderRadius: 1.5 }
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ mb: 1.5 }}
              InputProps={{
                sx: { borderRadius: 1.5 }
              }}
            />

            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Link
                href={`${_ENV_VARIABLES.B18_APP_URL}/auth/forgot-password`}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                height: 44,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem'
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}

export default LoginForm
